import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Stack,
  IconButton,
  Link,
  Breadcrumbs,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  Update,
  Visibility,
  Refresh,
  Replay,
  Edit,
  Person,
  Autorenew,
} from '@mui/icons-material';
import { useEnvironment } from '../../hooks/useEnvironments';
import { useConfiguredEnvironments, useCanEditEnvironment } from '../../contexts/EnvironmentsContext';
import { usePools } from '../../contexts/PoolsContext';
import { useConfig } from '../../contexts/ConfigContext';
import { canManageLease, isLeaseActive } from '@finvari/deliver';
import EnvironmentFormDialog from '../../components/EnvironmentFormDialog';
import EnvironmentStatusSection from '../../components/EnvironmentStatusSection';
import { useDeploymentStatus, useSetupTasksForRollout } from '../../contexts/DeploymentStatusContext';
import { usePullRequests } from '../../contexts/PullRequestsContext';
import { useMainCommits } from '../../contexts/MainCommitsContext';
import {
  formatDate,
  getDuration,
  getCloudBuildUrl,
  getEnvironmentOutOfDateStatus
} from '../../utils/deploymentUtils';
import {
  createDeploymentForMain,
  createDeploymentForPullRequest,
  retryDeployment,
  extendLease,
  releaseLease
} from '../../firebase';
import {
  getEnvironmentStatusColor,
  getDeploymentStatusIcon,
  getDeploymentStatusColor,
  createEnvironmentNavigationHandlers,
} from '../../utils/environmentUtils';
import TestSuiteCard from "./testExecutions/TestSuiteCard";
import { useTestSuites } from "../../utils/testUtils";
import SetupTaskCard from '../../components/SetupTaskCard';

const EnvironmentDetail: React.FC = () => {
  const { environmentId } = useParams<{ environmentId: string }>();
  const navigate = useNavigate();
  const [deployingLatest, setDeployingLatest] = React.useState(false);
  const [retryingModule, setRetryingModule] = React.useState<string | null>(null);
  const [deployingLatestModule, setDeployingLatestModule] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);

  const { environmentsById } = useConfiguredEnvironments();
  const { poolsById } = usePools();
  const { userProfile } = useConfig();
  // Raw stored document (config fields the computed EnvironmentStatus omits: owner, guardrails, status cache).
  const environmentDoc = environmentId ? environmentsById[environmentId] : undefined;
  const canEditEnv = useCanEditEnvironment();
  const canEdit = !!environmentDoc && canEditEnv(environmentDoc);

  // Lease panel state for pooled environments.
  const [leaseBusy, setLeaseBusy] = React.useState(false);
  const [leaseError, setLeaseError] = React.useState<string | null>(null);
  const lease = environmentDoc?.lease;
  const leaseActive = isLeaseActive(lease);
  const leasePerms = canManageLease({ userProfile, lease });
  const isLeaseHolder = leaseActive && lease?.holderUid === userProfile?.uid;
  // Direct actions on a pooled environment (deploy, retry, run tests, prepare) are reserved for the
  // current lease holder. Once the lease expires or is resigned, isLeaseHolder is false and every
  // such affordance is locked out — you must re-acquire the environment by deploying to the pool.
  const poolLockout = !!environmentDoc?.poolId && !isLeaseHolder;
  const poolLockReason =
    'This pooled environment is not leased to you. Deploy to the pool to acquire it, or wait for its lease holder.';

  const handleExtendLease = async () => {
    if (!environmentId || leaseBusy) return;
    setLeaseBusy(true);
    setLeaseError(null);
    try {
      await extendLease(environmentId);
    } catch (e: any) {
      setLeaseError(e?.message || 'Failed to extend lease');
    } finally {
      setLeaseBusy(false);
    }
  };

  const handleReleaseLease = async () => {
    if (!environmentId || leaseBusy) return;
    setLeaseBusy(true);
    setLeaseError(null);
    try {
      await releaseLease(environmentId);
    } catch (e: any) {
      setLeaseError(e?.message || 'Failed to release lease');
    } finally {
      setLeaseBusy(false);
    }
  };

  // Get environment with full operational status
  const {
    environment,
    loading,
    error,
    hasAnyOperationInFlight,
    hasSetupTaskInFlight,
    isDeploying
  } = useEnvironment(environmentId);

  const {
    liveDeploymentsByEnvironment,
    getModuleDeploymentsForEnvironment,
    getDeploymentConsistencyInfo
  } = useDeploymentStatus();
  const { pullRequests } = usePullRequests();
  const { mainCommits } = useMainCommits();
  const liveRolloutForSuites = liveDeploymentsByEnvironment.get(environmentId || '')?.rolloutId;
  const { testSuites, loading: testSuitesLoading, error: testSuitesError, refresh: refreshTestSuites } = useTestSuites(liveRolloutForSuites);

  // Get setup tasks for the current rollout (the live in-progress rollout drives retry + setup-task
  // display; the persisted activeRollout denorm is used only for the current-rollout summary).
  const liveRolloutId = liveRolloutForSuites;
  const { setupTasks } = useSetupTasksForRollout(liveRolloutId);

  const {
    handleSourceClick,
    handleDeploymentClick,
    handleBackToEnvironments
  } = createEnvironmentNavigationHandlers(navigate);

  // Handle deploy latest
  const handleDeployLatest = async () => {
    if (deployingLatest || !environment) return; // Prevent multiple clicks
    
    setDeployingLatest(true);
    
    try {
      const firstDeployment = allDeployments[0];
      if (!firstDeployment) return;

      console.log(`Deploying latest version to environment: ${environment.environmentId}`);
      
      let result;
      if (firstDeployment.prNumber && firstDeployment.prNumber > 0) {
        // This is a PR deployment - deploy the latest version of the PR
        result = await createDeploymentForPullRequest(
          firstDeployment.prNumber, 
          environment.environmentId
        );
        console.log('PR deployment started:', result);
      } else {
        // This is a main branch deployment - deploy the latest main
        result = await createDeploymentForMain(environment.environmentId);
        console.log('Main deployment started:', result);
      }
      
      // TODO: Show success message to user
      // The deployment status will be updated automatically through the contexts
    } catch (error) {
      console.error('Error starting deployment:', error);
      // TODO: Show error message to user
    } finally {
      setDeployingLatest(false);
    }
  };

  // Handle per-module retry (re-deploys from the rollout's branch/commit)
  const handleRetryModule = async (moduleName: string) => {
    if (!liveRolloutId || retryingModule) return;
    setRetryingModule(moduleName);
    try {
      await retryDeployment(liveRolloutId, moduleName);
    } catch (error) {
      console.error(`Error retrying module ${moduleName}:`, error);
    } finally {
      setRetryingModule(null);
    }
  };

  // Handle per-module deploy latest (re-deploys from the rollout's branch, which will pick up the latest commit)
  const handleDeployLatestModule = async (moduleName: string) => {
    if (!liveRolloutId || deployingLatestModule) return;
    setDeployingLatestModule(moduleName);
    try {
      await retryDeployment(liveRolloutId, moduleName);
    } catch (error) {
      console.error(`Error deploying latest for module ${moduleName}:`, error);
    } finally {
      setDeployingLatestModule(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading environment details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!environment) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Environment "{environmentId}" not found.
        </Alert>
      </Container>
    );
  }

  const liveRollout = liveDeploymentsByEnvironment.get(environment.environmentId);
  const moduleDeployments = getModuleDeploymentsForEnvironment(environment.environmentId);
  const consistencyInfo = getDeploymentConsistencyInfo(moduleDeployments);

  if (moduleDeployments.size === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {environment.environmentName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {environment.gcpProjectId}
            </Typography>
          </Box>
        </Box>

        <Alert severity="info">
          No deployments found for this environment.
        </Alert>
      </Container>
    );
  }

  const allDeployments = Array.from(moduleDeployments.values());

  // Check if successful deployments are out of date
  const outOfDateInfo = getEnvironmentOutOfDateStatus(
    moduleDeployments,
    pullRequests,
    mainCommits
  );
  const isOutOfDate = environment.status === 'ready' && outOfDateInfo.isOutOfDate;

  // Get PR information from any deployment
  const firstDeployment = allDeployments[0];
  const prNumber = firstDeployment.prNumber;
  const prTitle = firstDeployment.prTitle;
  const branch = firstDeployment.branch;

  // Calculate deployment timing
  const earliestStart = new Date(Math.min(...allDeployments.map(d => new Date(d.createdAt).getTime())));
  const allFinished = allDeployments.every(d => d.finishedAt);
  const latestFinish = allFinished ? new Date(Math.max(...allDeployments.map(d => new Date(d.finishedAt!).getTime()))) : null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToEnvironments}
            sx={{ textDecoration: 'none' }}
          >
            Environments
          </Link>
          <Typography variant="body2" color="text.primary">
            {environment.environmentName}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {environment.environmentName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {environment.gcpProjectId}
            </Typography>
            {environment.description && (
              <Typography variant="body1" color="text.secondary">
                {environment.description}
              </Typography>
            )}
            {environmentDoc?.ownerUid && (
              <Chip
                icon={<Person />}
                size="small"
                variant="outlined"
                color="info"
                label={`Personal — owned by ${environmentDoc.ownerEmail ?? environmentDoc.ownerUid}`}
                sx={{ mt: 1 }}
              />
            )}
            {environment.activeRollout?.autoDeployOnPush && (
              <Tooltip title="Redeploys automatically when new commits land on this source, until something else is deployed here.">
                <Chip
                  icon={<Autorenew />}
                  size="small"
                  variant="outlined"
                  color="info"
                  label={`Autodeploying ${
                    environment.activeRollout.prNumber
                      ? `PR #${environment.activeRollout.prNumber}`
                      : environment.activeRollout.branch
                  }`}
                  sx={{ mt: 1, ml: environmentDoc?.ownerUid ? 1 : 0 }}
                />
              </Tooltip>
            )}
          </Box>
          {canEdit && (
            <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {environmentDoc && (
        <EnvironmentFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          environment={environmentDoc}
        />
      )}

      {/* Pool membership + lease panel (pooled environments only) */}
      {environmentDoc?.poolId && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={`Pool: ${poolsById[environmentDoc.poolId]?.name ?? environmentDoc.poolId}`}
            subheader={
              leaseActive && lease
                ? `Held by ${lease.holderEmail} — expires ${formatDate(lease.expiresAt)}`
                : 'Available — no active lease'
            }
            action={
              <Chip
                size="small"
                color={leaseActive ? 'warning' : 'success'}
                variant="outlined"
                label={leaseActive ? 'Leased' : 'Available'}
              />
            }
          />
          {(leasePerms.canExtend || leasePerms.canResign || leasePerms.canForceRelease || isLeaseHolder) && (
            <CardContent>
              {isLeaseHolder && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You hold this lease — you can deploy to it directly below for the life of the lease.
                </Typography>
              )}
              {leaseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {leaseError}
                </Alert>
              )}
              <Stack direction="row" spacing={1}>
                {leaseActive && leasePerms.canExtend && (
                  <Button variant="outlined" size="small" disabled={leaseBusy} onClick={handleExtendLease}>
                    Extend lease ({lease?.leaseHours}h)
                  </Button>
                )}
                {leaseActive && leasePerms.canResign && (
                  <Button variant="outlined" size="small" color="warning" disabled={leaseBusy} onClick={handleReleaseLease}>
                    Resign lease
                  </Button>
                )}
                {leaseActive && leasePerms.canForceRelease && !leasePerms.canResign && (
                  <Button variant="outlined" size="small" color="error" disabled={leaseBusy} onClick={handleReleaseLease}>
                    Force-release lease
                  </Button>
                )}
              </Stack>
            </CardContent>
          )}
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Environment Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Environment Status"
          action={
            <Chip
              label={environment.status}
              color={getEnvironmentStatusColor(environment.status)}
              size="small"
            />
          }
        />
        <CardContent>
          {/* Environment Summary */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              handleSourceClick(firstDeployment.prNumber, firstDeployment.branch);
            }}
          >
            <Stack spacing={1}>
              {/* Operational status lives once in the card header chip (top right). This area shows
                  the current rollout's source; the out-of-date "Deploy Latest" affordance stays since
                  it is an action, not a duplicated status label. */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Current rollout
                </Typography>
                {isOutOfDate && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeployLatest();
                    }}
                    disabled={deployingLatest || hasAnyOperationInFlight || poolLockout}
                    color="warning"
                    title={
                      poolLockout
                        ? poolLockReason
                        : hasAnyOperationInFlight
                        ? 'Wait for active operations to complete'
                        : undefined
                    }
                  >
                    {deployingLatest ? 'Deploying...' : 'Deploy Latest'}
                  </Button>
                )}
              </Box>

              {prNumber ? (
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    PR #{prNumber}
                  </Typography>
                  {prTitle && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {prTitle}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" fontFamily="monospace">
                  {branch}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                {allDeployments.length} modules deployed
                {latestFinish ? (
                  <> • Completed in {getDuration(earliestStart.toISOString(), latestFinish.toISOString())}</>
                ) : (
                  <> • Started at {formatDate(earliestStart.toISOString())}</>
                )}
              </Typography>
            </Stack>
          </Paper>

          {/* Consistency Warning */}
          {!consistencyInfo.isConsistent && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Mixed Deployments
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Not all modules are from the same deployment.
                  Modules <strong>{consistencyInfo.inconsistentModules.join(', ')}</strong> are from different PR/branch.
                </Typography>
              </Stack>
            </Alert>
          )}

          {environmentDoc && <EnvironmentStatusSection environment={environmentDoc} canEdit={canEdit} prNumber={prNumber} />}
        </CardContent>
      </Card>

      {/* Setup Task Card */}
      <SetupTaskCard setupTasks={setupTasks} hasActiveOperations={hasAnyOperationInFlight} rolloutId={liveRolloutId} environmentId={environmentId} lockReason={poolLockout ? poolLockReason : undefined} />

      {/* Test Execution Card */}
      {!!liveRollout && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Test Suites"
            subheader="Run post-deployment tests on this environment"
            action={
              <IconButton
                onClick={refreshTestSuites}
                disabled={testSuitesLoading}
                title="Refresh test suite definitions"
                aria-label="Refresh test suite definitions"
                size="small"
              >
                {testSuitesLoading ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            }
          />
          <CardContent>
            {testSuitesError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {testSuitesError}
              </Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {testSuites.map(suite => (
                <TestSuiteCard
                  key={suite.id}
                  testSuite={suite}
                  environmentId={environment.environmentId}
                  rolloutId={liveRollout.rolloutId}
                  isDeployingOrSetup={isDeploying || hasSetupTaskInFlight}
                  lockReason={poolLockout ? poolLockReason : undefined}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Module Status Card */}
      <Card>
        <CardHeader
          title="Module Status"
          subheader={`${allDeployments.length} modules deployed`}
        />
        <CardContent>
          <List>
            {Array.from(moduleDeployments)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([module, deployment]) => {
                const isInconsistent = consistencyInfo.inconsistentModules.includes(module);
                
                // Check if this individual module is out of date
                let moduleIsOutOfDate = false;
                if ((deployment.status === 'success' || deployment.status === 'failed') && deployment.commitHash) {
                  if (deployment.prNumber && deployment.prNumber > 0) {
                    const pr = pullRequests.find(p => p.pr.number === deployment.prNumber);
                    if (pr && pr.lastCommit) {
                      moduleIsOutOfDate = deployment.commitHash !== pr.lastCommit.hash;
                    }
                  } else {
                    const latestMainCommit = mainCommits.find(commit => commit.order === 0);
                    if (latestMainCommit) {
                      moduleIsOutOfDate = deployment.commitHash !== latestMainCommit.hash;
                    }
                  }
                }
                
                // Determine display status and color for this module
                const moduleDisplayStatus = moduleIsOutOfDate ? 'out of date' : deployment.status;
                const moduleDisplayColor = moduleIsOutOfDate ? 'secondary' : getDeploymentStatusColor(deployment.status);
                const moduleIcon = moduleIsOutOfDate ? <Update fontSize="small" /> : getDeploymentStatusIcon(deployment.status);
                
                return (
                  <ListItem
                    key={module}
                    component="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeploymentClick(deployment.deploymentId);
                    }}
                    sx={{
                      border: isInconsistent ? '1px solid' : 'none',
                      borderColor: 'warning.main',
                      borderRadius: 1,
                      mb: 1,
                      p: 2,
                      bgcolor: 'background.default',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" fontWeight={600} color="text.primary">
                            {module}
                          </Typography>
                          <Chip
                            icon={moduleIcon}
                            label={moduleDisplayStatus.replace('_', ' ')}
                            color={moduleDisplayColor}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Stack spacing={1}>
                          {/* Show PR/branch info only for inconsistent modules */}
                          {isInconsistent && (
                            <Typography variant="body2" color="warning.main">
                              {deployment.prNumber ? (
                                <>PR #{deployment.prNumber}: {deployment.prTitle}</>
                              ) : (
                                <>Branch: {deployment.branch}</>
                              )}
                            </Typography>
                          )}
                          
                          {/* Deployment timing */}
                          {deployment.finishedAt && (
                            <Typography variant="body2" color="text.secondary">
                              Duration: {getDuration(deployment.createdAt, deployment.finishedAt)}
                            </Typography>
                          )}
                          
                          {/* Build ID and commit info */}
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {deployment.buildId && (
                              <Typography variant="caption" color="text.secondary">
                                Build: {deployment.buildId}
                              </Typography>
                            )}
                            {deployment.commitHash && (
                              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                Commit: {deployment.commitHash.substring(0, 8)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      {deployment.status === 'success' && moduleIsOutOfDate && (
                        <Tooltip title={poolLockout ? poolLockReason : 'Deploy latest commit'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Deploy latest commit for ${module}`}
                              onClick={e => {
                                e.stopPropagation();
                                handleDeployLatestModule(module);
                              }}
                              disabled={hasAnyOperationInFlight || deployingLatestModule !== null || retryingModule !== null || poolLockout}
                            >
                              {deployingLatestModule === module ? <CircularProgress size={18} /> : <Update fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {deployment.status === 'failed' && liveRolloutId && (
                        <Tooltip title={poolLockout ? poolLockReason : 'Retry this module'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Retry ${module}`}
                              onClick={e => {
                                e.stopPropagation();
                                handleRetryModule(module);
                              }}
                              disabled={hasAnyOperationInFlight || retryingModule !== null || deployingLatestModule !== null || poolLockout}
                            >
                              {retryingModule === module ? <CircularProgress size={18} /> : <Replay fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {!!deployment.buildId && (
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={`View build for ${module}`}
                          onClick={e => {
                            e.stopPropagation();
                            window.open(getCloudBuildUrl(deployment.buildId!, deployment.environment), '_blank');
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
          </List>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Last updated: {formatDate(environment.lastUpdated)}
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EnvironmentDetail;
